import { useState, useEffect, useCallback } from "react";
import { cache } from "../services/cache";
import { dashboardService } from "../services/dashboardService";
import { CACHE_KEYS} from "../constants/constants";

export function useDashboardData() {
  const [datasets, setDatasets] = useState({
    employees: [],
    departments: [],
    assets: [],
    leaves: [],
    onboarding: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  const loadOne = useCallback(async (key, fetcher, forceRefresh) => {
    if (!forceRefresh) {
      const cached = cache.read(key);
      if (cached) return { data: cached.data, lastUpdated: cached.lastUpdated };
    }
    const data = await fetcher();
    const record = cache.write(key, data);
    return { data: record.data, lastUpdated: record.lastUpdated };
  }, []);

  const load = useCallback(
    async (forceRefresh = false) => {
      forceRefresh ? setRefreshing(true) : setLoading(true);
      setError(null);
      try {
        const [emp, dept, assets, leaves, onboarding] = await Promise.all([
          loadOne(CACHE_KEYS.employees, dashboardService.getEmployees, forceRefresh),
          loadOne(CACHE_KEYS.departments, dashboardService.getDepartments, forceRefresh),
          loadOne(CACHE_KEYS.assets, dashboardService.getAssets, forceRefresh),
          loadOne(CACHE_KEYS.leaves, dashboardService.getLeaves, forceRefresh),
          loadOne(CACHE_KEYS.onboarding, dashboardService.getOnboarding, forceRefresh),
        ]);
        setDatasets({
          employees: emp.data,
          departments: dept.data,
          assets: assets.data,
          leaves: leaves.data,
          onboarding: onboarding.data,
        });
        setLastUpdated(emp.lastUpdated);
      } catch (e) {
        setError("Failed to load dashboard data. Please retry.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [loadOne]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);
  

  return { ...datasets, loading, refreshing, error, lastUpdated, refresh };
}